import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';
interface Product {
  title: string;
  editionLabel: string;
  priceText: string;
  thumbnailUrl: string;
  weight?: number; // Used for fallback probability
}
interface ToastNotification {
  id: number;
  name: string;
  city: string;
  product: Product;
}
const NAMES = ['María', 'Ana', 'Laura', 'Carmen', 'Isabel', 'Patricia', 'Sofía', 'Lucía', 'Elena', 'Daniela', 'Valentina', 'Camila', 'Andrea', 'Gabriela', 'Paula', 'Fernanda', 'Alejandra', 'Carolina', 'Diana', 'Natalia', 'Claudia', 'Rosa', 'Verónica', 'Adriana', 'Mónica', 'Silvia', 'Lorena', 'Paola', 'Jessica', 'Karla', 'Mariana', 'Renata', 'Ximena', 'Valeria', 'Catalina', 'Julieta'];
const CITIES = ['Miami', 'Los Angeles', 'Houston', 'New York', 'San Antonio', 'Chicago', 'Dallas', 'Phoenix', 'San Diego', 'San Jose', 'Austin', 'El Paso', 'Denver', 'Orlando', 'Tampa', 'Las Vegas', 'Tucson', 'Albuquerque', 'Sacramento', 'San Francisco', 'Atlanta', 'Charlotte', 'Raleigh'];
function scanPageProducts(): Product[] {
  const products: Product[] = [];
  const seenTitles = new Set<string>();
  const addProduct = (p: Product) => {
    if (!seenTitles.has(p.title)) {
      seenTitles.add(p.title);
      products.push(p);
    }
  };
  // Strategy 1: Look for JSON-LD structured data
  const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
  jsonLdScripts.forEach(script => {
    try {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product' && data.offers) {
        const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
        addProduct({
          title: data.name || 'Product',
          editionLabel: '',
          priceText: `$${parseFloat(offer.price).toFixed(2)} ${offer.priceCurrency || 'USD'}`,
          thumbnailUrl: Array.isArray(data.image) ? data.image[0] : data.image || ''
        });
      }
    } catch (e) {
      /* skip invalid JSON-LD */
    }
  });
  // Strategy 2: Scan #pricing section for specific product structures
  const pricingSection = document.getElementById('pricing');
  if (pricingSection) {
    // Look for Offer Stack items (they have a specific structure with a line-through price)
    const offerItems = pricingSection.querySelectorAll('.flex.items-center.gap-3.bg-cream-white\\/60');
    offerItems.forEach(item => {
      const titleEl = item.querySelector('p');
      const priceEl = item.querySelector('.line-through');
      const imgEl = item.querySelector('img');
      if (titleEl && priceEl && imgEl) {
        addProduct({
          title: titleEl.textContent?.trim() || '',
          editionLabel: 'Bono Exclusivo',
          priceText: priceEl.textContent?.trim() || '',
          thumbnailUrl: imgEl.src || ''
        });
      }
    });
    // Look for Order Bumps
    const bumpItems = pricingSection.querySelectorAll('.bump-shimmer, .bg-sage-green\\/5');
    bumpItems.forEach(item => {
      const titleEl = item.querySelector('h4');
      const priceTextEl = item.querySelector('.text-dusty-rose.uppercase');
      const imgEl = item.querySelector('img');
      if (titleEl && priceTextEl && imgEl) {
        const priceMatch = priceTextEl.textContent?.match(/\$\d+(\.\d{2})?/);
        const price = priceMatch ? `${priceMatch[0]} USD` : '$6.39 USD';
        addProduct({
          title: titleEl.textContent?.trim() || '',
          editionLabel: 'Complemento',
          priceText: price,
          thumbnailUrl: imgEl.src || ''
        });
      }
    });
    // Add main product if we found the hero image
    const mainImg = pricingSection.querySelector('img[src*="mk-2"]');
    if (mainImg) {
      addProduct({
        title: 'Pack Código Hormonal 28D',
        editionLabel: 'Pack Completo',
        priceText: '$14.99 USD',
        thumbnailUrl: (mainImg as HTMLImageElement).src
      });
    }
  }
  // Strategy 3: Broader DOM scan if we still don't have enough products
  if (products.length < 2) {
    const allImages = document.querySelectorAll('img[src*="mk-2"], img[src*="bn-"], img[src*="order_"]');
    allImages.forEach(img => {
      const src = (img as HTMLImageElement).src;
      let title = 'Pack Código Hormonal 28D';
      let price = '$14.99 USD';
      let label = 'Pack Completo';
      if (src.includes('bn-')) {
        title = 'Guía Adicional';
        price = '$37.00 USD';
        label = 'Bono';
      } else if (src.includes('order_')) {
        title = 'Protocolo Especial';
        price = '$6.39 USD';
        label = 'Complemento';
      }
      addProduct({
        title,
        editionLabel: label,
        priceText: price,
        thumbnailUrl: src
      });
    });
  }
  return products;
}
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'Pack Código Hormonal 28D',
  editionLabel: 'Pack Completo',
  priceText: '$14.99 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/iag9TmkkPqHt9rfpmjUPTT/mk-2.png',
  weight: 70
}, {
  title: 'EL PROTOCOLO "ADIÓS-CAFEÍNA"',
  editionLabel: 'Complemento',
  priceText: '$6.39 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/ghtdndXcR3UtrVh3Fc5MeC/order_mump-1.png',
  weight: 10
}, {
  title: 'LISTA DE SÚPER: COMPRA INTELIGENTE 28D',
  editionLabel: 'Complemento',
  priceText: '$6.39 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/aWPxHWRwr4rHdpZ97RpUmM/order_bump-2.png',
  weight: 10
}, {
  title: 'DIARIO DE SEÑALES BIOLÓGICAS',
  editionLabel: 'Complemento',
  priceText: '$6.39 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/jGkmDB2cMU6m8v2yumpNTU/order_bump-3.png',
  weight: 10
}];
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<ToastNotification | null>(null);
  const [visible, setVisible] = useState(false);
  const productsRef = useRef<Product[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const idCounterRef = useRef(0);
  const prefersReducedMotion = typeof window !== 'undefined' ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  const getRandomItem = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
  const getWeightedRandomProduct = (products: Product[]): Product => {
    // If products don't have weights (like scanned products), just pick randomly
    if (!products[0]?.weight) {
      return getRandomItem(products);
    }
    // Weighted selection for fallback products
    const totalWeight = products.reduce((sum, p) => sum + (p.weight || 0), 0);
    let random = Math.random() * totalWeight;
    for (const product of products) {
      random -= product.weight || 0;
      if (random <= 0) return product;
    }
    return products[0];
  };
  const getUniqueName = useCallback((): string => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = getRandomItem(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const showNotification = useCallback(() => {
    const products = productsRef.current.length > 0 ? productsRef.current : FALLBACK_PRODUCTS;
    const product = getWeightedRandomProduct(products);
    const name = getUniqueName();
    const city = getRandomItem(CITIES);
    idCounterRef.current += 1;
    setNotification({
      id: idCounterRef.current,
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
  }, [getUniqueName]);
  useEffect(() => {
    // Scan products after a short delay to let DOM render
    const scanTimeout = setTimeout(() => {
      productsRef.current = scanPageProducts();
    }, 1000);
    // First notification after 3 seconds
    const firstTimeout = setTimeout(() => {
      showNotification();
    }, 3000);
    return () => {
      clearTimeout(scanTimeout);
      clearTimeout(firstTimeout);
    };
  }, [showNotification]);
  useEffect(() => {
    if (!visible && notification) {
      // Schedule next notification at random interval 8-15s after current one hides
      const delay = 8000 + Math.random() * 7000;
      const timeout = setTimeout(() => {
        showNotification();
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [visible, notification, showNotification]);
  const motionVariants = prefersReducedMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 80
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: 80
    }
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] pointer-events-none" data-id="element-175">
      <AnimatePresence mode="wait" data-id="element-176">
        {visible && notification && <motion.div key={notification.id} initial={motionVariants.initial} animate={motionVariants.animate} exit={motionVariants.exit} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-warm-beige px-4 py-3 flex items-center gap-3 max-w-sm w-[340px] sm:w-[380px]" data-id="element-177">
            {notification.product.thumbnailUrl ? <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-warm-beige" data-id="element-178" /> : <div className="w-12 h-12 rounded-lg bg-soft-pink flex items-center justify-center flex-shrink-0" data-id="element-179">
                <ShoppingBag className="w-6 h-6 text-dusty-rose" data-id="element-180" />
              </div>}

            <div className="flex-grow min-w-0" data-id="element-181">
              <p className="text-sm text-deep-charcoal leading-snug" data-id="element-182">
                <span className="font-bold" data-id="element-183">{notification.name}</span> en{' '}
                <span className="font-semibold" data-id="element-184">{notification.city}</span>{' '}
                compró
              </p>
              <p className="text-xs text-deep-charcoal/70 truncate mt-0.5" data-id="element-185">
                {notification.product.editionLabel || notification.product.title}
                {' — '}
                <span className="font-bold text-dusty-rose" data-id="element-186">
                  {notification.product.priceText}
                </span>
              </p>
            </div>

            <div className="flex-shrink-0 w-2 h-2 rounded-full bg-sage-green animate-pulse" data-id="element-187" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}